<template>
  <div class="score-trend-container">
    <!-- 顶部工具栏 -->
    <el-card class="toolbar-card" :body-style="{ padding: '16px' }">
      <div class="toolbar-content">
        <div class="user-info">
          <h3>
            <i class="el-icon-data-analysis"></i>
            积分趋势分析
            <span v-if="userName" class="username-tag"> - {{ userName }}</span>
          </h3>
        </div>
        
        <div class="time-selector">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            size="small"
            :clearable="true"
            :picker-options="{ shortcuts: dateShortcuts }"
            @change="loadChartData">
          </el-date-picker>
        </div>
      </div>
    </el-card>

    <!-- 图表卡片 -->
    <el-card class="chart-card" v-loading="loading" :body-style="{ padding: '16px' }">
      <div class="chart-container" ref="chartContainer"></div>
    </el-card>

    <!-- 无数据提示 -->
    <el-card v-if="!loading && noData" class="no-data-card">
      <div class="no-data-content">
        <i class="el-icon-data-line"></i>
        <p>暂无积分趋势数据</p>
        <p class="hint">请先参加比赛、订正题目或发布文章获取积分</p>
      </div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'

export default {
  name: 'ScoreTrend',
  data() {
    return {
      loading: false,
      noData: false,
      chart: null,
      username: '',
      userName: '',
      dateRange: null,
      
      // 日期快捷选项
      dateShortcuts: [
        {
          text: '重置',
          onClick(picker) {
            picker.$emit('pick', null)
          }
        },
        {
          text: '最近一个月',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近三个月',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近半年',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近一年',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
            picker.$emit('pick', [start, end])
          }
        }
      ]
    }
  },
  mounted() {
    // 从路由参数获取用户名
    this.username = this.$route.query.username || ''
    this.userName = this.$route.query.name || ''
    
    // 初始化图表
    this.initChart()
    
    // 加载数据（默认最近一个月）
    const end = new Date()
    const start = new Date()
    start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
    // 格式化日期为 YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    this.dateRange = [formatDate(start), formatDate(end)]
    this.loadChartData()
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 初始化图表
    initChart() {
      if (this.chart) {
        this.chart.dispose()
      }
      this.chart = echarts.init(this.$refs.chartContainer)
    },
    
    // 加载图表数据
    async loadChartData() {
      if (!this.dateRange || this.dateRange.length === 0) {
        // 重置时加载全部数据
        this.dateRange = null
      }
      
      this.loading = true
      this.noData = false
      
      try {
        const params = {
          username: this.username || undefined
        }
        
        // 如果选择了日期范围，传递具体的开始和结束日期
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_date = this.dateRange[0]
          params.end_date = this.dateRange[1]
        }
        // 否则不传日期参数，后端会返回全部数据
        
        const response = await request({
          url: '/api/user/score/trend',
          method: 'get',
          params
        })
        
        if (response.code === 200) {
          const data = response.data
          
          // 检查是否有数据
          if (!data.dates || data.dates.length === 0) {
            this.noData = true
            return
          }
          
          this.renderChart(data)
        } else {
          this.$message.error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('加载图表数据失败:', error)
        this.$message.error('加载数据失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    // 渲染图表
    renderChart(data) {
      const option = {
        title: {
          text: '积分趋势分析',
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
            color: '#303133'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          },
          formatter: (params) => {
            let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`
            
            // 总积分（折线图）
            const totalScore = params.find(p => p.seriesName === '总积分')
            if (totalScore) {
              result += `<div style="margin-bottom: 4px;">
                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${totalScore.color};margin-right:5px;"></span>
                ${totalScore.seriesName}: <b>${totalScore.value}</b>
              </div>`
            }
            
            // 增量详情
            result += `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e4e7ed;">日增量详情：</div>`
            
            const deltas = params.filter(p => p.seriesName !== '总积分')
            let totalDelta = 0
            
            deltas.forEach(item => {
              if (item.value !== 0) {
                totalDelta += item.value
                result += `<div style="margin-top: 2px;">
                  <span style="display:inline-block;width:10px;height:10px;background:${item.color};margin-right:5px;"></span>
                  ${item.seriesName}: <b style="color: ${item.value > 0 ? '#67C23A' : '#F56C6C'};">${item.value > 0 ? '+' : ''}${item.value}</b>
                </div>`
              }
            })
            
            result += `<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e4e7ed; font-weight: 600;">
              总增量: <b style="color: ${totalDelta > 0 ? '#67C23A' : (totalDelta < 0 ? '#F56C6C' : '#909399')};">${totalDelta > 0 ? '+' : ''}${totalDelta}</b>
            </div>`
            
            return result
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e4e7ed',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            color: '#606266',
            fontSize: 13
          }
        },
        legend: {
          data: ['总积分', '参赛积分', '题目积分', '排名积分', '订正积分', '复盘积分', '文章积分'],
          top: 40,
          left: 'center',
          itemGap: 20,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '50px',
          right: '50px',
          top: '100px',
          bottom: '60px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: data.dates.map(date => date.substring(5)),  // 只显示月-日
          axisLabel: {
            fontSize: 11,
            color: '#606266',
            rotate: 45
          },
          axisLine: {
            lineStyle: {
              color: '#e4e7ed'
            }
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '总积分',
            position: 'left',
            axisLabel: {
              formatter: '{value}',
              fontSize: 11,
              color: '#606266'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: '#409EFF'
              }
            },
            splitLine: {
              lineStyle: {
                color: '#f0f0f0'
              }
            }
          },
          {
            type: 'value',
            name: '日增量',
            position: 'right',
            axisLabel: {
              formatter: '{value}',
              fontSize: 11,
              color: '#606266'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: '#67C23A'
              }
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '总积分',
            type: 'line',
            yAxisIndex: 0,
            data: data.total_scores,
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 3,
              color: '#409EFF'
            },
            itemStyle: {
              color: '#409EFF',
              borderWidth: 2,
              borderColor: '#fff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
              ])
            },
            emphasis: {
              focus: 'series',
              itemStyle: {
                borderWidth: 3,
                shadowBlur: 10,
                shadowColor: 'rgba(64, 158, 255, 0.5)'
              }
            }
          },
          {
            name: '参赛积分',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.game_score_deltas,
            itemStyle: {
              color: '#E6A23C'
            }
          },
          {
            name: '题目积分',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.question_score_deltas,
            itemStyle: {
              color: '#67C23A'
            }
          },
          {
            name: '排名积分',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.rank_score_deltas,
            itemStyle: {
              color: '#F56C6C'
            }
          },
          {
            name: '订正积分',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.fix_score_deltas,
            itemStyle: {
              color: '#909399'
            }
          },
          {
            name: '复盘积分',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.replay_score_deltas,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '文章积分',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.article_score_deltas,
            itemStyle: {
              color: '#9370DB'
            }
          }
        ]
      }
      
      this.chart.setOption(option, true)
    },
    
    // 窗口大小变化处理
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.score-trend-container {
  padding: 20px;
  min-height: calc(100vh - 80px);
  background-color: #f5f7fa;

  .toolbar-card {
    margin-bottom: 20px;
    
    .toolbar-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      
      .user-info {
        h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #303133;
          display: flex;
          align-items: center;
          
          i {
            margin-right: 8px;
            color: #409EFF;
          }
          
          .username-tag {
            font-size: 14px;
            color: #909399;
            font-weight: 400;
          }
        }
      }
      
      .time-selector {
        display: flex;
        align-items: center;
        gap: 12px;
      }
    }
  }

  .chart-card {
    .chart-container {
      width: 100%;
      height: 500px;
    }
  }

  .no-data-card {
    .no-data-content {
      text-align: center;
      padding: 60px 20px;
      color: #909399;
      
      i {
        font-size: 64px;
        margin-bottom: 20px;
        display: block;
        color: #c0c4cc;
      }
      
      p {
        font-size: 16px;
        margin: 8px 0;
        
        &.hint {
          font-size: 14px;
          color: #c0c4cc;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .score-trend-container {
    padding: 12px;
    
    .toolbar-card .toolbar-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      
      .time-selector {
        width: 100%;
      }
    }
    
    .chart-card .chart-container {
      height: 400px;
    }
  }
}
</style>
